// The staff boundary (guide, rules 6 and 12): one lookup of who is signed in, for every staff
// page below. The API's staff chain decides; this only routes on its answer — no session goes
// to sign-in, a session no authenticator code confirmed goes to enrolment or back to sign-in.
import { createFileRoute, Outlet, redirect, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { AuthCard } from "@/components/auth-card";
import { StaffShell } from "@/components/staff-shell";
import { SignOutButton } from "@/features/auth/sign-out/button";
import { useSignOut } from "@/features/auth/sign-out/mutation";
import { currentStaffQuery } from "@/features/staff/queries";
import { ROLE_LABELS } from "@/features/staff/roles";
import { staffTotpState } from "@/features/staff/session";
import { ApiError } from "@/lib/api-client";
import { m } from "@/lib/paraglide/messages.js";

export const Route = createFileRoute("/staff/_authenticated")({
  beforeLoad: async ({ context, location }) => {
    try {
      return { staffMember: await context.queryClient.ensureQueryData(currentStaffQuery()) };
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (error.status === 401) throw redirect({ to: "/staff/login", search: { redirect: location.href } });
      if (error.code === "totp_required") {
        if ((await staffTotpState()) === "not-enrolled") throw redirect({ to: "/staff/enrol" });
        throw redirect({ to: "/staff/login", search: { redirect: location.href } });
      }
      throw error;
    }
  },
  staticData: { crumb: m.staff_nav_home },
  component: StaffLayout,
  errorComponent: BoundaryError,
});

function StaffLayout() {
  const { staffMember } = Route.useRouteContext();
  const router = useRouter();
  const signOut = useSignOut();
  return (
    <StaffShell
      user={staffMember}
      roleLabel={ROLE_LABELS[staffMember.role]()}
      onSignOut={() => signOut(() => router.navigate({ to: "/staff/login" }))}
    >
      <Outlet />
    </StaffShell>
  );
}

function BoundaryError(_props: ErrorComponentProps) {
  const router = useRouter();
  return (
    <AuthCard title={m.common_error_title()} description={m.common_error_description()} area="staff">
      <SignOutButton onSignedOut={() => router.navigate({ to: "/staff/login" })} />
    </AuthCard>
  );
}
